import { logger } from "../../../util/Logger";
import { ExportAccumulator, ProductInstance, WallExportData } from "./types";
import { isMatchingWithInstance } from "./utils";

export class BalconyExporter {
  constructor(
    private readonly profileData: any,
    private readonly glassData: any,
    private readonly sizeInfo: any,
    private readonly profileColor: string,
    private readonly glassColor: string,
    private readonly name: string
  ) {}

  private createProfileInstance(): ProductInstance | null {
    if (!this.sizeInfo || !this.profileData) {
      logger.error("Size info is not available");
      return null;
    }

    return {
      productId: this.profileData.id,
      id: crypto.randomUUID(),
      attributes: {
        color: this.profileColor,
        width: this.sizeInfo.baseWidth,
      },
    };
  }

  private createGlassInstance(): ProductInstance | null {
    if (!this.glassData) return null;

    return {
      productId: this.glassData.id,
      id: crypto.randomUUID(),
      attributes: {
        color: this.glassColor,
        height: this.sizeInfo.glassHeight - this.sizeInfo.glassLowerProfile,
        thickness: this.sizeInfo.glassDepth,
        width: this.sizeInfo.glassWidth,
      },
      count: this.sizeInfo.glassCount,
    };
  }

  private updateProductDatas(accumulated: ExportAccumulator): void {
    if (
      !accumulated.profileProductDatas.find(
        (profile) => profile.id === this.profileData.id
      )
    ) {
      accumulated.profileProductDatas.push(this.profileData);
    }

    if (
      this.glassData &&
      !accumulated.glassProductDatas.find(
        (glass) => glass.id === this.glassData.id
      )
    ) {
      accumulated.glassProductDatas.push(this.glassData);
    }
  }

  private updateGlassInstance(
    glassInstance: ProductInstance,
    accumulated: ExportAccumulator
  ): ProductInstance {
    const matchingInstance = accumulated.glassProductInstances.find((glass) =>
      isMatchingWithInstance(glass, glassInstance)
    );

    if (!matchingInstance) {
      accumulated.glassProductInstances.push(glassInstance);
      return glassInstance;
    }

    matchingInstance.count! += glassInstance.count!;
    return matchingInstance;
  }

  private updateProfileInstance(
    profileInstance: ProductInstance,
    accumulated: ExportAccumulator
  ): ProductInstance {
    const matchingInstance = accumulated.profileProductInstances.find(
      (profile) => isMatchingWithInstance(profile, profileInstance)
    );

    if (!matchingInstance) {
      accumulated.profileProductInstances.push(profileInstance);
      return profileInstance;
    }

    matchingInstance.attributes.width += profileInstance.attributes.width;
    return matchingInstance;
  }

  private updateWallData(
    walls: WallExportData[],
    profileInstance: ProductInstance,
    glassInstance: ProductInstance | null
  ): void {
    const wallData = walls.find((w) => w.uuid === this.name);
    if (wallData) {
      wallData.attributes = {
        profileId: profileInstance.id,
        ...(glassInstance && { glassId: glassInstance.id }),
      };
    }
  }

  export(accumulated: ExportAccumulator, walls: WallExportData[]): void {
    const profileInstance = this.createProfileInstance();
    if (!profileInstance) return;

    const glassInstance = this.createGlassInstance();

    this.updateProductDatas(accumulated);

    const updatedProfileInstance = this.updateProfileInstance(
      profileInstance,
      accumulated
    );
    const updatedGlassInstance = glassInstance
      ? this.updateGlassInstance(glassInstance, accumulated)
      : null;

    this.updateWallData(walls, updatedProfileInstance, updatedGlassInstance);
  }
}
